import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';
import Swal from 'sweetalert2';

/* ─── Portal Tooltip ─── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });

  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div className={`proj-tooltip proj-tooltip-${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

/* ─── Main Component ─── */
function Projects() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [general, setGeneral] = useState({ experiments: [], exp_count: 0, published_count: 0 });
  const [unassignedExperiments, setUnassignedExperiments] = useState([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddExpModal, setShowAddExpModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [choiceType, setChoiceType] = useState(null);
  const [deleteExpId, setDeleteExpId] = useState(null);
  const [deleteExpName, setDeleteExpName] = useState('');
  const [deleteProjectId, setDeleteProjectId] = useState(null);
  const [deleteProjectName, setDeleteProjectName] = useState('');

  const [projectName, setProjectName] = useState('');
  const [expTitle, setExpTitle] = useState('');
  const [expDescription, setExpDescription] = useState('');
  const [selectedExpIds, setSelectedExpIds] = useState([]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('/qurator/projects');
      if (res.data.success) {
        setGeneral(res.data.general);
        setProjects(res.data.projects);
        setUnassignedExperiments(res.data.unassigned_experiments);
        setTotalProjects(res.data.total_projects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to load projects' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const toggleProject = (id) => {
    const next = new Set(expandedProjects);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedProjects(next);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) return Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter a project name' });
    try {
      const res = await API.post('/qurator/projects', { project_name: projectName.trim() });
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Created!', text: res.data.message, timer: 2000, showConfirmButton: false });
        setShowCreateModal(false);
        setProjectName('');
        fetchProjects();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to create project' }); }
  };

  const openAddExpModal = (projectId) => {
    setCurrentProjectId(projectId);
    setChoiceType(null);
    setExpTitle(''); setExpDescription(''); setSelectedExpIds([]);
    setShowAddExpModal(true);
  };

  const backToChoice = () => {
    setChoiceType(null);
    setExpTitle(''); setExpDescription(''); setSelectedExpIds([]);
  };

  const handleCreateExperiment = async (type) => {
    if (!expTitle.trim()) return Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter an experiment title' });
    if (type === 'manual') {
      const p = new URLSearchParams({ project_id: currentProjectId || '', title: expTitle.trim(), description: expDescription.trim() });
      navigate(`/drafts?${p.toString()}`);
      return;
    }
    try {
      const res = await API.post(`/qurator/projects/${currentProjectId || 0}/experiments`, {
        exp_title: expTitle.trim(), exp_description: expDescription.trim(), creation_type: 'freestyle'
      });
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Created!', text: res.data.message, timer: 2000, showConfirmButton: false });
        setShowAddExpModal(false);
        setExpTitle(''); setExpDescription('');
        fetchProjects();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to create experiment' }); }
  };

  const handleAddExistingExperiments = async (e) => {
    e.preventDefault();
    if (selectedExpIds.length === 0) return Swal.fire({ icon: 'warning', title: 'Required', text: 'Please select at least one experiment' });
    try {
      const res = await API.post(`/qurator/projects/${currentProjectId || 0}/experiments`, { experiment_ids: selectedExpIds });
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Added!', text: res.data.message, timer: 2000, showConfirmButton: false });
        setShowAddExpModal(false);
        setSelectedExpIds([]);
        fetchProjects();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to add experiments' }); }
  };

  const toggleExpSelection = (id) => {
    const next = [...selectedExpIds];
    const idx = next.indexOf(id);
    idx > -1 ? next.splice(idx, 1) : next.push(id);
    setSelectedExpIds(next);
  };

  const handleRemoveExperiment = async (e) => {
    e.preventDefault();
    let projectId = null;
    for (const p of projects) {
      if (p.experiments?.some(x => x.prt_id === deleteExpId)) { projectId = p.project_id; break; }
    }
    if (projectId === null && general.experiments.some(x => x.prt_id === deleteExpId)) projectId = 0;
    if (projectId === null) return Swal.fire({ icon: 'error', title: 'Error', text: 'Experiment not found' });

    try {
      const res = await API.delete(`/qurator/projects/${projectId}/experiments/${deleteExpId}`);
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Removed', text: res.data.message, timer: 2000, showConfirmButton: false });
        setShowDeleteModal(false);
        setDeleteExpId(null); setDeleteExpName('');
        fetchProjects();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to remove experiment' }); }
  };

  const openEditProjectModal = (projectId, projectName) => {
    setCurrentProjectId(projectId);
    setCurrentProjectName(projectName);
    setProjectName(projectName);
    setShowEditProjectModal(true);
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) return Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter a project name' });
    try {
      const res = await API.put(`/qurator/projects/${currentProjectId}`, { project_name: projectName.trim() });
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Updated!', text: res.data.message, timer: 2000, showConfirmButton: false });
        setShowEditProjectModal(false);
        setProjectName('');
        setCurrentProjectId(null);
        setCurrentProjectName('');
        fetchProjects();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to update project' }); }
  };

  const openDeleteProjectModal = (projectId, projectName) => {
    setDeleteProjectId(projectId);
    setDeleteProjectName(projectName);
    setShowDeleteProjectModal(true);
  };

  const handleDeleteProject = async (e) => {
    e.preventDefault();
    try {
      const res = await API.delete(`/qurator/projects/${deleteProjectId}`);
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Deleted!', text: res.data.message, timer: 2000, showConfirmButton: false });
        setShowDeleteProjectModal(false);
        setDeleteProjectId(null);
        setDeleteProjectName('');
        fetchProjects();
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.message || 'Failed to delete project' }); }
  };

  const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const isLive = (v) => v === 1 || v === '1';

  /* Calculate accurate counts from experiments arrays */
  const getPublishedCount = (experiments) => {
    if (!experiments || !Array.isArray(experiments)) return 0;
    return experiments.filter(exp => isLive(exp.is_live)).length;
  };

  const getInProgressCount = (experiments) => {
    if (!experiments || !Array.isArray(experiments)) return 0;
    return experiments.filter(exp => !isLive(exp.is_live)).length;
  };

  /* Computed totals for stat cards */
  const generalPublished = getPublishedCount(general.experiments);
  const generalInProgress = getInProgressCount(general.experiments);
  const totalSims   = general.experiments.length + projects.reduce((s, p) => s + ((p.experiments && p.experiments.length) || 0), 0);
  const totalPub    = generalPublished + projects.reduce((s, p) => s + getPublishedCount(p.experiments), 0);
  const totalDraft  = totalSims - totalPub;

  /* Reusable simulation list renderer */
  const renderExperiments = (experiments, isGeneral = false) => (
    experiments.length > 0 ? experiments.map(exp => (
      <div key={exp.prt_id} className="exp-item">
        <div className="exp-item-row">
          <div className="exp-title-block">
            <h6 className="exp-title">{exp.draft_title}</h6>
            {exp.exp_description && (
              <p className="exp-desc">{exp.exp_description}</p>
            )}
          </div>
          <div className="exp-actions">
            <span className={`exp-badge ${isLive(exp.is_live) ? 'exp-badge-published' : 'exp-badge-draft'}`}>
              {isLive(exp.is_live) ? <><i className="bi bi-patch-check-fill"></i> Published</> : <><i className="bi bi-pencil"></i> Draft</>}
            </span>
            {isLive(exp.is_live) && (
              <Tooltip text="Run this simulation">
                <button className="exp-icon-btn exp-btn-play" onClick={() => navigate(`/play-simulation?id=${exp.prt_id}`)}>
                  <i className="bi bi-play-fill"></i>
                </button>
              </Tooltip>
            )}
            <Tooltip text="View simulation details">
              <button className="exp-icon-btn exp-btn-view" onClick={() => navigate(`/draft-read?viewid=${exp.prt_id}`)}>
                <i className="bi bi-eye"></i>
              </button>
            </Tooltip>
            <Tooltip text="Edit simulation">
              <button className="exp-icon-btn exp-btn-edit" onClick={() => navigate(`/draft-edit?editid=${exp.prt_id}`)}>
                <i className="bi bi-pencil"></i>
              </button>
            </Tooltip>
            {!isGeneral && (
              <Tooltip text="Remove from project">
                <button className="exp-icon-btn exp-btn-remove" onClick={() => { setDeleteExpId(exp.prt_id); setDeleteExpName(exp.draft_title); setShowDeleteModal(true); }}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="exp-meta">
          <span><i className="bi bi-calendar3"></i> {fmt(exp.date)}</span>
          <span><i className="bi bi-cpu-fill"></i> Simulation</span>
          {isLive(exp.is_live)
            ? <span className="exp-meta-live"><i className="bi bi-circle-fill"></i> Live</span>
            : <span className="exp-meta-draft"><i className="bi bi-circle"></i> In Progress</span>
          }
        </div>
      </div>
    )) : (
      <div className="proj-empty" style={{ padding: '1.5rem 1rem' }}>
        <i className="bi bi-cpu"></i>
        <h6>{isGeneral ? 'No unassigned simulations' : 'No simulations yet'}</h6>
        <p>{isGeneral ? 'All simulations are organised in projects' : 'Click "+ Add Simulation" to get started'}</p>
      </div>
    )
  );

  if (loading) {
    return (
      <QuratorLayout>
        <div className="projects-loading">
          <div className="qrt-loading"></div>
          <p>Loading projects...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="projects-wrap">

        {/* ── Hero ── */}
        <div className="proj-hero">
          <div className="proj-hero-inner">
            <div>
              <h1 className="proj-hero-title">
                <i className="bi bi-folder2-open"></i> Projects Management
              </h1>
              <p className="proj-hero-sub">
                Organise and manage your research projects and experiments in one place.
              </p>
            </div>
            <Tooltip text="Create a new research project to organise your experiments">
              <button className="proj-hero-btn" onClick={() => setShowCreateModal(true)}>
                <i className="bi bi-plus-lg"></i> New Project
              </button>
            </Tooltip>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <Tooltip text="Total research projects you have created" position="bottom">
              <div className="proj-stat proj-stat-orange">
                <div className="proj-stat-ico"><i className="bi bi-folder-fill"></i></div>
                <div>
                  <div className="proj-stat-num">{totalProjects}</div>
                  <div className="proj-stat-lbl">Total Projects</div>
                </div>
              </div>
            </Tooltip>
          </div>
          <div className="col-6 col-md-3">
            <Tooltip text="Total simulations across all projects" position="bottom">
              <div className="proj-stat proj-stat-blue">
                <div className="proj-stat-ico"><i className="bi bi-cpu-fill"></i></div>
                <div>
                  <div className="proj-stat-num">{totalSims}</div>
                  <div className="proj-stat-lbl">Total Simulations</div>
                </div>
              </div>
            </Tooltip>
          </div>
          <div className="col-6 col-md-3">
            <Tooltip text="Simulations published and available to Qure users" position="bottom">
              <div className="proj-stat proj-stat-green">
                <div className="proj-stat-ico"><i className="bi bi-patch-check-fill"></i></div>
                <div>
                  <div className="proj-stat-num">{totalPub}</div>
                  <div className="proj-stat-lbl">Published</div>
                </div>
              </div>
            </Tooltip>
          </div>
          <div className="col-6 col-md-3">
            <Tooltip text="Simulations still in draft — not yet published" position="bottom">
              <div className="proj-stat proj-stat-amber">
                <div className="proj-stat-ico"><i className="bi bi-pencil-square"></i></div>
                <div>
                  <div className="proj-stat-num">{totalDraft}</div>
                  <div className="proj-stat-lbl">In Progress</div>
                </div>
              </div>
            </Tooltip>
          </div>
        </div>

        {/* ── Projects Section ── */}
        <div className="proj-section">
          <div className="proj-section-head">
            <h5 className="proj-section-title">
              <i className="bi bi-folder-fill"></i> Your Projects
            </h5>
            <span className="proj-count-badge">{totalProjects} Project{totalProjects !== 1 ? 's' : ''}</span>
          </div>

          <div className="proj-list">

            {/* General (Default) Project */}
            <div className={`proj-card proj-card-general ${expandedProjects.has('general') ? 'expanded' : ''}`}>
              <div className="proj-card-head">
                <div className="proj-card-info">
                  <h6 className="proj-card-name">
                    General <span className="proj-default-badge">Default</span>
                  </h6>
                  <div className="proj-card-meta">
                    <span className="proj-meta-chip"><i className="bi bi-cpu-fill"></i> {general.experiments.length} Simulation{general.experiments.length !== 1 ? 's' : ''}</span>
                    <span className="proj-meta-chip proj-meta-published"><i className="bi bi-patch-check-fill"></i> {generalPublished} Published</span>
                    <span className="proj-meta-chip proj-meta-progress"><i className="bi bi-pencil-fill"></i> {generalInProgress} In Progress</span>
                  </div>
                </div>
                <div className="proj-card-actions">
                  <Tooltip text="Add a new or existing simulation to the General project">
                    <button className="proj-btn-add" onClick={() => openAddExpModal(0)}>
                      <i className="bi bi-plus-lg"></i> Add Simulation
                    </button>
                  </Tooltip>
                  <Tooltip text={expandedProjects.has('general') ? 'Collapse simulations' : 'Expand simulations'}>
                    <button
                      className={`proj-expand-btn ${expandedProjects.has('general') ? 'active' : ''}`}
                      onClick={() => toggleProject('general')}
                    >
                      <i className={`bi bi-chevron-${expandedProjects.has('general') ? 'up' : 'down'}`}></i>
                    </button>
                  </Tooltip>
                </div>
              </div>
              <div className={`proj-experiments ${expandedProjects.has('general') ? 'show' : ''}`}>
                {renderExperiments(general.experiments, true)}
              </div>
            </div>

            {/* User Projects */}
            {projects.length > 0 ? projects.map(project => (
              <div key={project.project_id} className={`proj-card ${expandedProjects.has(project.project_id) ? 'expanded' : ''}`}>
                <div className="proj-card-head">
                  <div className="proj-card-info">
                    <h6 className="proj-card-name">{project.project_name}</h6>
                    <div className="proj-card-meta">
                      <span className="proj-meta-date"><i className="bi bi-calendar3"></i> {fmt(project.created_date)}</span>
                      <span className="proj-meta-chip"><i className="bi bi-cpu-fill"></i> {(project.experiments && project.experiments.length) || 0} Simulation{((project.experiments && project.experiments.length) || 0) !== 1 ? 's' : ''}</span>
                      <span className="proj-meta-chip proj-meta-published"><i className="bi bi-patch-check-fill"></i> {getPublishedCount(project.experiments)} Published</span>
                      <span className="proj-meta-chip proj-meta-progress"><i className="bi bi-pencil-fill"></i> {getInProgressCount(project.experiments)} In Progress</span>
                    </div>
                  </div>
                  <div className="proj-card-actions">
                    <Tooltip text="Assign this project's copilots to Qure users via the Calendar">
                      <Link to={`/calendar?project_id=${project.project_id}&open_modal=true`} className="proj-btn-assign">
                        <i className="bi bi-calendar-check"></i> Assign
                      </Link>
                    </Tooltip>
                    <Tooltip text="Add a new or existing simulation to this project">
                      <button className="proj-btn-add" onClick={() => openAddExpModal(project.project_id)}>
                        <i className="bi bi-plus-lg"></i> Add Simulation
                      </button>
                    </Tooltip>
                    <Tooltip text="Edit project name">
                      <button className="proj-btn-edit" onClick={() => openEditProjectModal(project.project_id, project.project_name)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                    </Tooltip>
                    <Tooltip text="Delete project">
                      <button className="proj-btn-delete" onClick={() => openDeleteProjectModal(project.project_id, project.project_name)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </Tooltip>
                    <Tooltip text={expandedProjects.has(project.project_id) ? 'Collapse simulations' : 'Expand simulations'}>
                      <button
                        className={`proj-expand-btn ${expandedProjects.has(project.project_id) ? 'active' : ''}`}
                        onClick={() => toggleProject(project.project_id)}
                      >
                        <i className={`bi bi-chevron-${expandedProjects.has(project.project_id) ? 'up' : 'down'}`}></i>
                      </button>
                    </Tooltip>
                  </div>
                </div>
                <div className={`proj-experiments ${expandedProjects.has(project.project_id) ? 'show' : ''}`}>
                  {renderExperiments(project.experiments || [], false)}
                </div>
              </div>
            )) : (
              <div className="proj-empty">
                <i className="bi bi-folder-plus"></i>
                <h5>No Custom Projects Yet</h5>
                <p>Create your first project to start organising your experiments</p>
                <button className="proj-cta-btn" onClick={() => setShowCreateModal(true)}>
                  <i className="bi bi-plus-lg"></i> Create Project
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════════════ MODALS ═══════════════ */}

      {/* Create Project */}
      {showCreateModal && (
        <div className="proj-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="proj-modal" onClick={e => e.stopPropagation()}>
            <div className="proj-modal-header">
              <div className="proj-modal-title"><i className="bi bi-folder-plus"></i> Create New Project</div>
              <button className="proj-modal-close" onClick={() => setShowCreateModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleCreateProject}>
              <div className="proj-modal-body">
                <div className="mb-3">
                  <label className="proj-form-label">Project Name</label>
                  <input
                    type="text"
                    className="proj-form-control"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                    placeholder="Enter project name"
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="proj-modal-footer">
                <button type="button" className="proj-btn proj-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  <i className="bi bi-x"></i> Cancel
                </button>
                <button type="submit" className="proj-btn proj-btn-primary">
                  <i className="bi bi-folder-plus"></i> Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Experiment */}
      {showAddExpModal && (
        <div className="proj-modal-overlay" onClick={() => { setShowAddExpModal(false); backToChoice(); }}>
          <div className="proj-modal proj-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="proj-modal-header">
              <div className="proj-modal-title"><i className="bi bi-plus-circle"></i> Add Simulation</div>
              <button className="proj-modal-close" onClick={() => { setShowAddExpModal(false); backToChoice(); }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="proj-modal-body">

              {/* Choice selection */}
              {!choiceType && (
                <>
                  <p className="text-center mb-2" style={{ color: '#718096', fontSize: '0.92rem' }}>
                    How would you like to add a simulation?
                  </p>
                  <div className="proj-choice-grid">
                    <div className="proj-choice-card" onClick={() => setChoiceType('new')}>
                      <div className="proj-choice-icon"><i className="bi bi-file-earmark-plus"></i></div>
                      <h5>Create New</h5>
                      <p>Start a fresh experiment from scratch</p>
                    </div>
                    <div className="proj-choice-card" onClick={() => setChoiceType('existing')}>
                      <div className="proj-choice-icon"><i className="bi bi-folder2-open"></i></div>
                      <h5>Add Existing</h5>
                      <p>Select from your unassigned experiments</p>
                    </div>
                  </div>
                </>
              )}

              {/* New experiment form */}
              {choiceType === 'new' && (
                <div>
                  <div className="mb-3">
                    <label className="proj-form-label">Experiment Title *</label>
                    <input type="text" className="proj-form-control" value={expTitle}
                      onChange={e => setExpTitle(e.target.value)} placeholder="Enter experiment title" autoFocus />
                  </div>
                  <div className="mb-3">
                    <label className="proj-form-label">Description (optional)</label>
                    <textarea className="proj-form-control" rows="3" value={expDescription}
                      onChange={e => setExpDescription(e.target.value)} placeholder="Describe your experiment"></textarea>
                  </div>
                  <div className="proj-type-btns">
                    <button type="button" className="proj-type-btn" onClick={() => handleCreateExperiment('manual')}>
                      <i className="bi bi-pencil-square"></i> Complete Editing Manually
                    </button>
                    <button type="button" className="proj-type-btn" onClick={() => handleCreateExperiment('freestyle')}>
                      <i className="bi bi-lightning-fill"></i> Free Style (Quick Save)
                    </button>
                  </div>
                  <div className="mt-3">
                    <button type="button" className="proj-btn proj-btn-secondary" onClick={backToChoice}>
                      <i className="bi bi-arrow-left"></i> Back
                    </button>
                  </div>
                </div>
              )}

              {/* Existing experiments */}
              {choiceType === 'existing' && (
                <form onSubmit={handleAddExistingExperiments}>
                  <p style={{ color: '#718096', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    Select experiments to add to this project:
                  </p>
                  <div className="proj-exp-list">
                    {unassignedExperiments.length > 0 ? unassignedExperiments.map(exp => (
                      <div key={exp.prt_id} className="proj-exp-check">
                        <input
                          type="checkbox"
                          id={`exp_${exp.prt_id}`}
                          checked={selectedExpIds.includes(exp.prt_id)}
                          onChange={() => toggleExpSelection(exp.prt_id)}
                        />
                        <label htmlFor={`exp_${exp.prt_id}`}>
                          {exp.draft_title}
                          <small style={{ display: 'block', color: '#a0aec0', fontSize: '0.78rem' }}>{fmt(exp.date)}</small>
                        </label>
                      </div>
                    )) : (
                      <p style={{ textAlign: 'center', color: '#a0aec0', padding: '1rem 0', margin: 0 }}>
                        No unassigned experiments available
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                    <button type="button" className="proj-btn proj-btn-secondary" onClick={backToChoice}>
                      <i className="bi bi-arrow-left"></i> Back
                    </button>
                    <button type="submit" className="proj-btn proj-btn-primary">
                      <i className="bi bi-plus-circle"></i> Add Selected
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Project */}
      {showEditProjectModal && (
        <div className="proj-modal-overlay" onClick={() => { setShowEditProjectModal(false); setProjectName(''); setCurrentProjectId(null); setCurrentProjectName(''); }}>
          <div className="proj-modal" onClick={e => e.stopPropagation()}>
            <div className="proj-modal-header">
              <div className="proj-modal-title"><i className="bi bi-pencil"></i> Edit Project</div>
              <button className="proj-modal-close" onClick={() => { setShowEditProjectModal(false); setProjectName(''); setCurrentProjectId(null); setCurrentProjectName(''); }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleEditProject}>
              <div className="proj-modal-body">
                <div className="mb-3">
                  <label className="proj-form-label">Project Name</label>
                  <input
                    type="text"
                    className="proj-form-control"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                    placeholder="Enter project name"
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="proj-modal-footer">
                <button type="button" className="proj-btn proj-btn-secondary" onClick={() => { setShowEditProjectModal(false); setProjectName(''); setCurrentProjectId(null); setCurrentProjectName(''); }}>
                  <i className="bi bi-x"></i> Cancel
                </button>
                <button type="submit" className="proj-btn proj-btn-primary">
                  <i className="bi bi-check-lg"></i> Update Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation */}
      {showDeleteProjectModal && (
        <div className="proj-modal-overlay" onClick={() => { setShowDeleteProjectModal(false); setDeleteProjectId(null); setDeleteProjectName(''); }}>
          <div className="proj-modal" onClick={e => e.stopPropagation()}>
            <div className="proj-modal-header danger">
              <div className="proj-modal-title"><i className="bi bi-exclamation-triangle"></i> Delete Project</div>
              <button className="proj-modal-close" onClick={() => { setShowDeleteProjectModal(false); setDeleteProjectId(null); setDeleteProjectName(''); }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleDeleteProject}>
              <div className="proj-modal-body" style={{ textAlign: 'center' }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '2.8rem', color: '#e74c3c', display: 'block', marginBottom: '0.75rem' }}></i>
                <h5 style={{ marginBottom: '0.5rem', color: '#2d3748' }}>Delete this project?</h5>
                <p style={{ color: '#4a5568', fontWeight: 600, fontSize: '1rem' }}>"{deleteProjectName}"</p>
                <p style={{ color: '#a0aec0', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>
                  All simulations in this project will be moved to the General project. This action cannot be undone.
                </p>
              </div>
              <div className="proj-modal-footer">
                <button type="button" className="proj-btn proj-btn-secondary" onClick={() => { setShowDeleteProjectModal(false); setDeleteProjectId(null); setDeleteProjectName(''); }}>
                  <i className="bi bi-x"></i> Cancel
                </button>
                <button type="submit" className="proj-btn proj-btn-danger">
                  <i className="bi bi-trash"></i> Delete Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Experiment Confirmation */}
      {showDeleteModal && (
        <div className="proj-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="proj-modal" onClick={e => e.stopPropagation()}>
            <div className="proj-modal-header danger">
              <div className="proj-modal-title"><i className="bi bi-exclamation-triangle"></i> Confirm Removal</div>
              <button className="proj-modal-close" onClick={() => setShowDeleteModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleRemoveExperiment}>
              <div className="proj-modal-body" style={{ textAlign: 'center' }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '2.8rem', color: '#FF9800', display: 'block', marginBottom: '0.75rem' }}></i>
                <h5 style={{ marginBottom: '0.5rem', color: '#2d3748' }}>Remove this experiment?</h5>
                <p style={{ color: '#4a5568', fontWeight: 600 }}>"{deleteExpName}"</p>
                <p style={{ color: '#a0aec0', fontSize: '0.85rem', margin: 0 }}>
                  The experiment will be moved to the General project.
                </p>
              </div>
              <div className="proj-modal-footer">
                <button type="button" className="proj-btn proj-btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <i className="bi bi-x"></i> Cancel
                </button>
                <button type="submit" className="proj-btn proj-btn-danger">
                  <i className="bi bi-trash"></i> Remove
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </QuratorLayout>
  );
}

export default Projects;
